'use client';
import { useState, useRef, ChangeEvent, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { usePdfTool } from '@/context/PdfToolContext';
import { useAnalytics } from '@/lib/analytics';

const PdfThumbnail = dynamic(() => import('@/components/PdfThumbnail'), { ssr: false });

type Range = {
  start: number;
  end: number;
  originalStr: string;
  isRemaining?: boolean;
};

interface SplitPDFClientProps {
  h1: string;
  subtitle: string;
  content: string;
}

export default function SplitPDFClient({ h1, subtitle, content }: SplitPDFClientProps) {
  useAnalytics('split');
  const { items, setItems, resetContext, setToolParams } = usePdfTool();
  const router = useRouter();
  
  const [isDraggingOverall, setIsDraggingOverall] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [activeTab, setActiveTab] = useState<'split' | 'extract'>('split');
  const [rangesInput, setRangesInput] = useState<string>('');

  useEffect(() => {
    resetContext();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetContext]);

  const addFile = async (newFiles: File[]) => {
    const pdfFiles = newFiles.filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) return;
    
    // Split tools only accept one file
    const file = pdfFiles[0];
    
    // Load PDF to get number of pages
    try {
      const { pdfjs } = await import('react-pdf');
      pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument(arrayBuffer).promise;
      setNumPages(pdf.numPages);
      
      setItems([{
        id: crypto.randomUUID(),
        file
      }]);
      setError(null);
    } catch (e) {
      console.error("PDF Load Error:", e);
      setError("Failed to load PDF. Please try another file.");
    }
  };

  // Handle Full Page Drag & Drop
  useEffect(() => {
    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer?.types.includes('Files')) return;
      setIsDraggingOverall(true);
    };
    
    const handleWindowDragLeave = (e: DragEvent) => {
      e.preventDefault();
      if (e.relatedTarget === null || e.clientX === 0 || e.clientY === 0) {
        setIsDraggingOverall(false);
      }
    };
    
    const handleWindowDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDraggingOverall(false);
      if (e.dataTransfer && e.dataTransfer.files) {
        addFile(Array.from(e.dataTransfer.files));
      }
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFile(Array.from(e.target.files));
    }
  };

  // Parse ranges in real time
  const parsedRanges = useMemo<Range[]>(() => {
    if (!rangesInput.trim()) {
      return numPages ? [{ start: 1, end: numPages, originalStr: 'default' }] : [];
    }
    const parts = rangesInput.split(',').map(p => p.trim()).filter(Boolean);
    const ranges: Range[] = [];
    
    for (const part of parts) {
      const matchRange = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (matchRange) {
        const start = parseInt(matchRange[1], 10);
        const end = parseInt(matchRange[2], 10);
        if (start > 0 && end >= start) {
          ranges.push({ start, end, originalStr: part });
        }
      } else {
        const matchSingle = part.match(/^(\d+)$/);
        if (matchSingle) {
          const num = parseInt(matchSingle[1], 10);
          if (num > 0) {
            ranges.push({ start: num, end: num, originalStr: part });
          }
        }
      }
    }

    // Add remaining pages if in split mode
    if (activeTab === 'split' && numPages && ranges.length > 0) {
      const used = new Set<number>();
      for (const r of ranges) {
        for (let p = Math.max(1, r.start); p <= Math.min(numPages, r.end); p++) {
          used.add(p);
        }
      }
      
      const unused: number[] = [];
      for (let p = 1; p <= numPages; p++) {
        if (!used.has(p)) unused.push(p);
      }
      
      if (unused.length > 0) {
        const subRanges: string[] = [];
        let i = 0;
        while (i < unused.length) {
          let start = unused[i];
          let end = start;
          while (i + 1 < unused.length && unused[i+1] === end + 1) {
            end = unused[i+1];
            i++;
          }
          if (start === end) subRanges.push(`${start}`);
          else subRanges.push(`${start}-${end}`);
          i++;
        }
        
        ranges.push({
          start: unused[0],
          end: unused[unused.length - 1],
          originalStr: `Remaining (${subRanges.join(', ')})`,
          isRemaining: true
        });
      }
    }

    return ranges;
  }, [rangesInput, numPages, activeTab]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers, commas, hyphens, and spaces
    const val = e.target.value.replace(/[^\d,\-\s]/g, '');
    setRangesInput(val);
  };

  const handleProcess = () => {
    if (parsedRanges.length === 0) {
      setError("Please enter valid page ranges.");
      return;
    }
    
    if (numPages !== null) {
      // Exclude remaining ranges from out of bounds check
      const outOfBounds = parsedRanges.some(r => !r.isRemaining && (r.start > numPages || r.end > numPages));
      if (outOfBounds) {
        setError(`One or more pages exceed the total page count (${numPages}).`);
        return;
      }
    }
    
    setToolParams({
      mode: activeTab,
      ranges: rangesInput.trim() ? rangesInput : `1-${numPages}`
    });
    
    router.push('/split-pdf/processing');
  };

  return (
    <div className={`container tool-page ${items.length > 0 ? 'app-mode-container' : ''}`}>
      <div className="tool-header">
        <h1>{h1}</h1>
        <p>{subtitle}</p>
      </div>

      <div className="tool-interface" style={items.length > 0 ? { display: 'flex', gap: '2rem', alignItems: 'flex-start' } : undefined}>
        {items.length === 0 && (
          <div 
            className={`upload-zone ${isDraggingOverall ? 'dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-icon">📄</div>
            <h3>Select PDF file / Drag & Drop</h3>
          </div>
        )}

        {items.length > 0 && (
          <div className="split-layout" style={{ width: '100%' }}>
            {/* Workspace Area */}
            <div className="split-workspace">
              {parsedRanges.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  Enter ranges in the right panel to see a preview.
                </div>
              ) : (
                parsedRanges.map((range, index) => (
                  <div key={index} className="range-block" style={range.isRemaining ? { borderColor: 'var(--primary-color)', backgroundColor: 'var(--surface-color)' } : undefined}>
                    <div className="range-block-header">
                      {range.isRemaining ? range.originalStr : `Range ${index + 1}`}
                    </div>
                    <div className="range-block-content">
                      <div className="pdf-tile" style={{ height: 'auto', padding: '1rem', width: '180px' }}>
                        <div className="pdf-tile-preview" style={{ border: 'none', background: 'transparent' }}>
                          <PdfThumbnail file={items[0].file} pageNumber={range.start} />
                        </div>
                        <div className="pdf-tile-name">{range.start}</div>
                      </div>
                      
                      {range.start !== range.end && (
                        <>
                          <div className="range-separator">•••</div>
                          <div className="pdf-tile" style={{ height: 'auto', padding: '1rem', width: '180px' }}>
                            <div className="pdf-tile-preview" style={{ border: 'none', background: 'transparent' }}>
                              <PdfThumbnail file={items[0].file} pageNumber={range.end} />
                            </div>
                            <div className="pdf-tile-name">{range.end}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Right Panel */}
            <div className="split-sidebar">
              <div className="split-tabs">
                <div 
                  className={`split-tab ${activeTab === 'split' ? 'active' : ''}`}
                  onClick={() => setActiveTab('split')}
                >
                  Split
                </div>
                <div 
                  className={`split-tab ${activeTab === 'extract' ? 'active' : ''}`}
                  onClick={() => setActiveTab('extract')}
                >
                  Extract Pages
                </div>
              </div>
              
              <div className="split-input-group">
                <label>
                  {activeTab === 'split' 
                    ? "Enter page number from where to split the pdf. Put comma( , ) for multiple splits."
                    : "Enter page or series to extract from this PDF"}
                </label>
                <input 
                  type="text" 
                  value={rangesInput}
                  onChange={handleInputChange}
                  placeholder="e.g. 1-4, 6-9, 10, 13"
                />
                <span className="split-example">(e.g 1-4, 6-9, 10, 13)</span>
                {numPages && (
                  <span className="split-example" style={{ marginTop: '0.5rem', display: 'block', color: 'var(--text-color)' }}>
                    Total Pages: {numPages}
                  </span>
                )}
              </div>
              
              <button 
                className="btn btn-primary" 
                style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}
                onClick={handleProcess}
                disabled={parsedRanges.length === 0}
              >
                {activeTab === 'split' ? 'SPLIT' : 'EXTRACT'}
              </button>
            </div>
          </div>
        )}

        <input 
          type="file" 
          accept=".pdf,application/pdf"
          className="hidden-input" 
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
      </div>
      
      {error && <div className="error-alert">{error}</div>}
      
      <div className="tool-content" dangerouslySetInnerHTML={{ __html: content }}>
      </div>
    </div>
  );
}
